/**
 * Maps raw MIME types to concise, user-friendly display labels.
 */

const MIME_LABEL_MAP: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "Excel (XLSX)",
  "application/vnd.ms-excel": "Excel (XLS)",
  "application/vnd.oasis.opendocument.spreadsheet": "ODS",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word (DOCX)",
  "application/msword": "Word (DOC)",
  "application/vnd.oasis.opendocument.text": "ODT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PowerPoint (PPTX)",
  "application/vnd.ms-powerpoint": "PowerPoint (PPT)",
  "application/vnd.oasis.opendocument.presentation": "ODP",
  "application/zip": "ZIP",
  "application/x-rar-compressed": "RAR",
  "application/x-7z-compressed": "7Z",
  "application/gzip": "GZ",
  "application/json": "JSON",
  "application/xml": "XML",
  "text/html": "HTML",
  "text/plain": "TXT",
  "text/csv": "CSV",
  "image/jpeg": "JPEG",
  "image/png": "PNG",
  "image/gif": "GIF",
  "image/webp": "WEBP",
  "image/svg+xml": "SVG",
  "audio/mpeg": "MP3",
  "audio/ogg": "OGG",
  "audio/wav": "WAV",
  "video/mp4": "MP4",
  "video/webm": "WEBM",
};

/**
 * Return a short, human-friendly label for a MIME type.
 *
 * Lookup order:
 *  1. Exact match in the known-label map.
 *  2. File extension extracted from the filename (if provided).
 *  3. Fallback to the subtype after "/" uppercased, capped to keep it short.
 */
export function getFriendlyMimeLabel(
  mime: string | undefined | null,
  filename?: string,
): string {
  if (!mime) {
    if (filename && filename.includes(".")) {
      const ext = filename.split(".").pop()?.toUpperCase();
      if (ext) return ext;
    }
    return "File";
  }

  const lower = mime.toLowerCase().split(";")[0].trim();

  const exact = MIME_LABEL_MAP[lower];
  if (exact) return exact;

  // Try to derive from filename extension
  if (filename && filename.includes(".")) {
    const ext = filename.split(".").pop()?.toUpperCase();
    if (ext && ext.length <= 5) return ext;
  }

  // Fallback: take the subtype and extract the last dot-segment
  // (e.g. "spreadsheetml.sheet" → "SHEET")
  const subtype = lower.split("/").pop() || "";
  const lastSegment = subtype.split(".").pop() || subtype;
  const cleaned = lastSegment.replace(/^x-/, "").toUpperCase();
  return cleaned.length <= 10 ? cleaned : cleaned.slice(0, 10);
}
